import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { clone, isEmpty } from '@helpers/arrays';
import { EmpyreanDialogType } from '@interfaces/types/empyrean-dialog-type';
import { DialogSaveObject } from '@models/dialog-save-object.d';
import { MenuItem } from '@models/menu-item';
import { PermissionsService } from '@services/auth/permissions.service';
import { WindowRef } from '@services/windowRef/window-ref.service';
import { ToastrService } from 'ngx-toastr';
import { NotificationsService } from '@services/notifications/notifications.service';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import Swal, { SweetAlertOptions } from 'sweetalert2';

const DEFAULT_SIDEPANEL = {
  width: '400px',
  visible: false,
};

@Injectable({ providedIn: 'root' })
export class MenuService {
  get isEditingAssumptions() {
    return this._isEditingAssumptions && this.selectedMenuItem.routerLink === 'simulation-assumptions';
  }

  set isEditingAssumptions(val: boolean) {
    this._isEditingAssumptions = val;

    // Ensure navigation to Assumptions
    setTimeout(() => this.selectMenuItem(this.simulationMenu.find(item => item.routerLink === 'simulation-assumptions')));
  }

  /**
   * @description Click Handler function triggered when the user clicks the "Save" button
   * on the dialog when is in a Model Settings Page
   */

  public set dialogSaveButton(newStatePiece: DialogSaveObject) {
    const currentState: DialogSaveObject = clone({ ...this.dialogSaveButton });
    let newState: DialogSaveObject = clone({ ...this.dialogSaveButton, ...newStatePiece });

    if (newState.page !== currentState.page) {
      newState = { ...this.saveButtonInitialState, ...newState };
    }

    if (!isEqual(currentState, newState)) {
      this.selectedMenuItem.canDeactivate = !newState.enabled;
      this.dialogSaveButton$.next(newState);
    }
  }
  public get dialogSaveButton(): DialogSaveObject {
    return this.dialogSaveButton$.value;
  }

  get mainPanelWidth() {
    return `calc(100% - ${this.sidePanelWidth})`;
  }

  public unusedAccountsWindowOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private router: Router,
    private readonly location: Location,
    private winRef: WindowRef,
    private permissions: PermissionsService,
    private toastrService: ToastrService
  ) {
    this.dataMenu = [
      { label: 'Lookup Tables', routerLink: 'lookup-data', canDeactivate: true, outlet: 'data-outlet' },

      {
        label: 'Liquidity Source Equations',
        routerLink: 'liquidity-equations',
        canDeactivate: true,
        outlet: 'data-outlet',
      },
      { label: 'Exogenous Equations', routerLink: 'exogenous-equations', canDeactivate: true, outlet: 'data-outlet' },
      { label: 'Auxiliary Equations', routerLink: 'auxiliary-equations', canDeactivate: true, outlet: 'data-outlet' },
    ];

    this.structureMenu = [
      {
        label: 'Balance Sheet Structure',
        routerLink: 'balance-sheet',
        canDeactivate: true,
        outlet: 'structure-outlet',
      },
      { label: 'Scenarios', routerLink: 'scenarios', canDeactivate: true, outlet: 'structure-outlet' },
      { label: 'Lookup Tables', routerLink: 'lookup-setup', canDeactivate: true, outlet: 'structure-outlet' },
      { label: 'Liquidity Sources', routerLink: 'liquidity-sources', canDeactivate: true, outlet: 'structure-outlet' },
      { label: 'Exogenous Lines', routerLink: 'exogenous-lines', canDeactivate: true, outlet: 'structure-outlet' },
      {
        label: 'Global Settings',
        routerLink: this.permissions.getPermission('GlobalSettings') ? 'global-settings' : '401',
        canDeactivate: true,
        outlet: 'structure-outlet',
        disabled: this.permissions.getPermission('GlobalSettings') !== 'W',
      },
    ];

    this.simulationMenu = [
      { label: 'Simulation Set', routerLink: 'simulation-set', canDeactivate: true, outlet: 'simulation-outlet' },
      { label: 'Assumptions', routerLink: 'simulation-assumptions', canDeactivate: true, outlet: 'simulation-outlet' },
    ];

    this.mainMenu = [{ label: 'Manage Result Sets', routerLink: 'results', canDeactivate: false, outlet: 'main-outlet' }];

    this.importerMenu = [
      {
        label: 'Import ALM Data Set',
        routerLink: 'sim-importer',
        canDeactivate: true,
        outlet: 'importer-outlet',
      },
    ];

    this.allMenus = [...this.dataMenu, ...this.structureMenu, ...this.simulationMenu, ...this.mainMenu, ...this.importerMenu];

    try {
      const jsonStr = this.permissions.getTabsFromSessionStorage();
      this.lastSelectedItemsRouterLinks = JSON.parse(jsonStr) as {
        data: string;
        structure: string;
        results: string;
        'sim-importer': string;
      };
    } catch (error) {
      // console.warn(`Error reading from Local storage`, error);
    }

    if (this.lastSelectedItemsRouterLinks === null) {
      this.lastSelectedItemsRouterLinks = {
        data: this.dataMenu[0].routerLink,
        structure: this.structureMenu[0].routerLink,
        results: this.mainMenu[0].routerLink,
        'sim-importer': this.importerMenu[0].routerLink,
        simulation: null,
      };
      this.permissions.saveTabsToSessionStorage(this.lastSelectedItemsRouterLinks);
    }
  }
  get selectedMenuItem() {
    return this.selectedMenuItem$.getValue();
  }

  set selectedMenuItem(menuItem: MenuItem) {
    if (!isEqual(menuItem, this.selectedMenuItem)) {
      this.selectedMenuItem$.next(menuItem);
    }
  }
  private _isEditingAssumptions = false;

  public selectedMenuItem$: BehaviorSubject<MenuItem> = new BehaviorSubject<MenuItem>(null);

  // public selectedMenuItem: MenuItem = null;

  public menuItems: Array<any>;
  public maximized$ = new BehaviorSubject<boolean>(false);

  private allMenus: MenuItem[] = [];
  private selectedMenu = null;
  private lastSelectedItemsRouterLinks = {};

  public dataMenu: MenuItem[];
  public structureMenu: MenuItem[];
  public simulationMenu: MenuItem[];
  public mainMenu: MenuItem[];
  public importerMenu: MenuItem[];

  toBeClosed$ = new BehaviorSubject<string>('');

  // Dialog Save button
  private saveButtonInitialState: DialogSaveObject = {
    page: null,
    enabled: false,
    clicked: false,
    // valid: true,
    valid: false,
    source: 'initialState',
    // page?: string;
    // enabled?: boolean;
    // valid?: boolean;
    // clicked?: boolean;
    // source?: string;
  };
  public dialogSaveButton$ = new BehaviorSubject<DialogSaveObject>(this.saveButtonInitialState);
  // --- End of Dialog Save Button

  // Deactivate Popup Emitter on close
  public deactivatePopupClosed$ = new Subject<boolean>();

  // SidePanel
  public isSidePanelVisible: boolean = DEFAULT_SIDEPANEL.visible;
  public sidePanelWidth: string = DEFAULT_SIDEPANEL.width;
  public readonly defaultSidePanelSettings = DEFAULT_SIDEPANEL;

  // Dialog Ref
  private dialog: MatDialog;
  public dialogRef: MatDialogRef<any>;

  public setDialogRef(dialogRef: MatDialogRef<any, any>): any {
    this.dialogRef = dialogRef;
  }

  public toggleMaximized() {
    const currentValue = this.maximized$.getValue();
    this.maximized$.next(!currentValue);
  }

  selectLastSavedMenuOrFirst(menu: string): MenuItem {
    this.selectedMenu = menu;
    const menuRouterLink = this.lastSelectedItemsRouterLinks[this.selectedMenu];
    let menuItemFound = this.allMenus.find(searchMenu => searchMenu.routerLink === menuRouterLink);
    if (!isEmpty(menuRouterLink) && !isEmpty(menuItemFound)) {
      // Bug fix 2940 with exception for Edit Assumptions View
      if (menu === 'simulation') {
        menuItemFound = this.isEditingAssumptions ? this.simulationMenu[1] : this.simulationMenu[0];
      }
      this.selectMenuItem(menuItemFound, false);
      return menuItemFound;
    } else {
      let menuItemToSave = null;
      // Selects the first item of the menu
      switch (menu) {
        case 'data':
          menuItemToSave = this.dataMenu[0];
          break;
        case 'structure':
          menuItemToSave = this.structureMenu[0];
          break;
        case 'simulation':
          menuItemToSave = this.simulationMenu[0];
          break;
        case 'results':
          menuItemToSave = this.mainMenu[0];
          break;
        case 'sim-importer':
          menuItemToSave = this.importerMenu[0];
          break;
        default:
          menuItemToSave = this.dataMenu[0];
          break;
      }
      this.lastSelectedItemsRouterLinks[this.selectedMenu] = menuItemToSave.routerLink;
      this.selectMenuItem(menuItemToSave);
      return menuItemToSave;
    }
  }

  public getMenu(menu: EmpyreanDialogType) {
    this.selectLastSavedMenuOrFirst(menu);
    switch (menu) {
      case 'data':
        return this.dataMenu;
      case 'structure':
        return this.structureMenu;
      case 'simulation':
        return this.simulationMenu;
      case 'results':
        return this.mainMenu;
      case 'sim-importer':
        return this.importerMenu;
      default:
        return this.dataMenu;
    }
  }

  public selectMenuItem(item: MenuItem, saveSelection = true) {
    if (!item) {
      item = this.selectedMenuItem;
    }

    if (!this.selectedMenuItem) {
      this.selectedMenuItem = item;
    }

    return this.tabClicked(item).then(navigationResult => {
      // Converting 'null' to false
      navigationResult = !!navigationResult;

      // If navigation succedeed (i.e. not blocked by deactivate guard)
      if (navigationResult === true) {
        // Store that selected menu item
        this.selectedMenuItem = item;
        this.selectedMenuItem.canDeactivate = true;

        // Set the proper outlet too
        this.selectedMenu = item.outlet.replace('-outlet', '');
        this.lastSelectedItemsRouterLinks[this.selectedMenu] = item.routerLink;

        // Set the Save Button if it's a Structure menu tab
        // (structure tabs are the only ones with save button)
        if (this.selectedMenu === 'structure') {
          this.dialogSaveButton = {
            page: item.routerLink,
            enabled: false,
            clicked: false,
            source: 'navigation on tabClicked (menu service)',
          };
        }

        // Save to local storage
        if (saveSelection && this.selectedMenuItem) {
          this.permissions.saveTabsToSessionStorage(this.lastSelectedItemsRouterLinks);
        }
      }
      return navigationResult;
    });
  }

  public tabClicked(item: MenuItem) {
    if (item.routerLink && item.outlet) {
      const navigateParams = [{ outlets: { [item.outlet]: [item.routerLink] } }];
      return this.router.navigate(navigateParams).then(r1 => {
        this.location.replaceState('/'); // `/${menuItem.routerLink}`
        return r1;
      });
    } else {
      return this.router.navigate([{ outlets: { [item.outlet]: null } }]).then(r2 => {
        this.location.replaceState('/');
        return true;
      });
    }
  }

  public navigateTo(routerLink: string) {
    const menuItem = this.allMenus.find(m => m.routerLink === routerLink);
    if (menuItem) {
      // Only navigate if current page can be deactivated
      this.canDeactivatePopup().then(canBeDeactivated => {
        if (canBeDeactivated) {
          if (this.selectedMenuItem.outlet !== menuItem.outlet) {
            this.closeDialog();
          }
          let elementId = '';
          switch (menuItem.outlet) {
            case 'data-outlet':
              elementId = 'btn_view_model_data';
              break;
            case 'structure-outlet':
              elementId = 'btn_view_model_structure';
              break;
            case 'simulation-outlet':
              elementId = 'btn_create_edit_simulation';
              break;
            case 'importer-outlet':
              elementId = 'btn_importer';
              break;
            default:
              elementId = 'page_header';
              break;
          }

          if (document.getElementById(elementId)) {
            document.getElementById(elementId).click();
          }

          // ['lookup-data', 'liquidity-equations', 'exogenous-equations']
          if (this.dataMenu.map(m => m.routerLink).includes(menuItem.routerLink)) {
            this.selectedMenuItem = menuItem;
            this.selectedMenu = menuItem.outlet.replace('-outlet', '');
            this.lastSelectedItemsRouterLinks[this.selectedMenu] = menuItem.routerLink;
          }
        } else {
          // console.log(`Cancelling navigation, cannot be deactivated (pending changes)`);
        }
      });
    }
  }

  public goToMenuItem(target: string) {
    const menuItemFound = this.allMenus.find(menuItem => menuItem.routerLink === target);
    this.router.navigate([{ outlets: { [menuItemFound.outlet]: null } }]);
    this.selectMenuItem(menuItemFound);
  }

  public closeDialog() {
    this.toastrService.clear();
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
      this.dialogSaveButton = { ...this.saveButtonInitialState, source: 'closeDialog' };
    }
  }

  public openDialog(menuItem: MenuItem) {
    this.tabClicked(menuItem);
  }

  public toggleSidePanel(defaultWidth: string = DEFAULT_SIDEPANEL.width) {
    this.isSidePanelVisible = !this.isSidePanelVisible;
    if (this.isSidePanelVisible) {
      this.sidePanelWidth = defaultWidth;
    } else {
      this.sidePanelWidth = '0px';
    }
    this.emitResize();
  }

  public emitResize(delay = 0) {
    setTimeout(() => this.winRef.nativeWindow.dispatchEvent(new Event('resize')), delay);
  }

  /**
   * @description This will emit the name of the tab that is being closed.
   * Components subscribed to this can act before being closed.
   */
  public prepareToClose() {
    this.toBeClosed$.next(this.selectedMenuItem.label);
  }

  /**
   * Toggles the guard for the window
   * @param toggle Set true to ask the user before leaving the page. False will disable this check
   */
  public selectedMenuItemCanDeactivate(toggle: boolean = null) {
    if (toggle !== null) {
      if (this.selectedMenuItem.canDeactivate !== toggle) {
        this.selectedMenuItem.canDeactivate = toggle;
        this.dialogSaveButton = {
          ...this.dialogSaveButton,
          enabled: !toggle,
          source: 'menu-service selectedMenuItemCanDeactivate',
        };
      }
    }
    return this.selectedMenuItem.canDeactivate;
  }

  /**
   * Fires up a popup before the component attempts to close so the user can stay in the page before losing any changes
   *
   * **'selectedMenuItemCanDeactivate'** must be called to track condition if component will fire popup.
   *
   * @example
   * this.menu.selectedMenuItemCanDeactivate(this.dirtyRows.length === 0);
   *
   * async canDeactivate(): Promise<boolean> {
   *   const message =
   *     'You have unsaved changes on this page. Please click Cancel to return to the page or continue to discard changes';
   *   return this.menu.canDeactivatePopup(message);
   * }
   */

  public async canDeactivatePopup(): Promise<boolean> {
    let title: string, text: string, confirmButtonColor: string, cancelButtonColor: string;
    let confirmButtonText = 'Continue';
    let cancelButtonText = 'Cancel';

    switch (this.selectedMenuItem.label) {
      case 'Import ALM Data Set':
        text = 'Closing this window will stop updates regarding the import status.';
        title = 'Dataset Import In Progress';
        confirmButtonText = 'Close this window';
        cancelButtonText = 'Stay on this window';
        confirmButtonColor = '#A9A9A9';
        cancelButtonColor = '#3085d6';
        break;
      default:
        text = 'You have unsaved changes on this page. Please click Cancel to return to the page or continue to discard changes';
        title = 'Unsaved changes';
    }

    // console.log("checking deactivate");
    if (!this.selectedMenuItem.canDeactivate) {
      return await Swal.fire({
        title: title,
        text: text,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowEscapeKey: true,
        confirmButtonColor, // If =null, use default color
        cancelButtonColor, // If =null, use default color
      } as SweetAlertOptions).then(async result => {
        this.deactivatePopupClosed$.next(!!result.value);
        return result.value;
      });
    } else {
      return true;
    }
  }
}
